import type { MigrationCaseEventDto } from '@/lib/api';

/**
 * Event-Typen, die dem User auf der Migration-Card und in der Historie
 * angezeigt werden. Alles, was nicht in dieser Allow-List steht, gilt als
 * technisches Rauschen (z. B. `office_draft_saved`, `favorite_set`,
 * `action_previewed`) und wird weder als Pill noch in der Historie
 * gerendert.
 *
 * Wird sowohl im `MigrationCaseGridCard` (Latest-Event-Pill) als auch im
 * `MigrationCaseHistoryModal` (Vollliste) verwendet. Backend nutzt eine
 * 1:1 gespiegelte Liste in `LegacyMigrationCaseService` für den
 * `latest_event`-Lookup beim Listen-Endpunkt.
 */
export const MIGRATION_DISPLAYABLE_EVENT_TYPES: ReadonlySet<string> = new Set([
  'case_created',
  'case_link_updated',
  'comment',
  'contact.not_reached',
  'contact.reached',
  'email_sent',
  'email_resent',
  'case_email_sent',
  'action_executed',
  'office_preview_confirmed',
  'office_migration_confirmed',
  'onboarding_started',
  'case_migrated',
  'contract_adjustment_customer_flow_sent',
  'contract_adjustment_customer_confirmed',
  'offer_sent',
  'offer_accepted',
  'offer_rejected',
  'order_confirmed',
]);

export const MIGRATION_EVENT_LABELS: Record<string, string> = {
  case_created: 'Case angelegt',
  case_link_updated: 'Datensatz verknüpft',
  comment: 'Kommentar',
  'contact.not_reached': 'Telefonisch nicht erreicht',
  'contact.reached': 'Telefonisch erreicht',
  email_sent: 'E-Mail gesendet',
  email_resent: 'E-Mail erneut gesendet',
  case_email_sent: 'E-Mail gesendet',
  action_executed: 'Aktion ausgeführt',
  office_preview_confirmed: 'Office-Vorschau bestätigt',
  office_migration_confirmed: 'Migration vom Office bestätigt',
  onboarding_started: 'Onboarding gestartet',
  case_migrated: 'Case abgeschlossen',
  contract_adjustment_customer_flow_sent: 'Vertragsanpassung an Kunden gesendet',
  contract_adjustment_customer_confirmed: 'Vertragsanpassung vom Kunden bestätigt',
  offer_sent: 'Angebot an Lehrer gesendet',
  offer_accepted: 'Angebot vom Lehrer angenommen',
  offer_rejected: 'Angebot vom Lehrer abgelehnt',
  order_confirmed: 'Auftrag vom Office bestätigt',
};

export const isDisplayableMigrationEvent = (eventType: string): boolean =>
  MIGRATION_DISPLAYABLE_EVENT_TYPES.has(eventType);

/**
 * Liefert das Anzeige-Label für ein Event. Für `comment`-Events wird der
 * eingegebene Kommentar-Text (falls vorhanden) inline an das Label gehängt,
 * damit man auf der Pill und in der Historie direkt den Inhalt sieht.
 */
export const formatMigrationEventLabel = (event: MigrationCaseEventDto): string => {
  const baseLabel = MIGRATION_EVENT_LABELS[event.event_type] ?? event.event_type;
  if (event.event_type === 'comment') {
    const text = typeof event.payload?.text === 'string' ? event.payload.text.trim() : '';
    if (text !== '') return `Kommentar: ${text}`;
  }
  return baseLabel;
};

/**
 * Formatiert den `created_at`-ISO-Stempel eines Events analog zu den
 * bestehenden Anzeigen ("dd.MM.yy, HH:mm").
 */
export const formatMigrationEventTimestamp = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};
